import type { NodeDefinition } from "../domain/types/index.js";
import { CASES } from "./cases.js";

const WIDTH = 7;
const LAST_ROW = 15;
const PATH_COUNT = 7;

const CASE_IDS = [
  "p21-capex", "p21-roi", "p21-automation-pilot", "p21-partner-selection", "p21-risk-assessment",
  "p21-data-integration", "p21-knowledge-transfer", "p21-resilience-build", "p21-automation-scale", "p21-contract-review",
  "p21-left-review", "p21-center-review", "p21-right-review",
  "p21-settlement-left", "p21-settlement-right", "p21-integration-left", "p21-integration-right",
] as const;

const CASE_POOLS = {
  // 最初の4段は、直接の進捗-2を持つ負荷を出さない。
  opening: ["p21-capex", "p21-risk-assessment"],
  openingFollowup: ["p21-capex", "p21-risk-assessment"],
  earlySafe: ["p21-capex", "p21-risk-assessment", "p21-resilience-build"],
  early: ["p21-automation-pilot", "p21-partner-selection", "p21-risk-assessment", "p21-data-integration", "p21-knowledge-transfer", "p21-resilience-build"],
  // 中盤は小規模の安全札と、先取りできる大型報酬を同居させる。
  mid: ["p21-automation-pilot", "p21-risk-assessment", "p21-automation-scale", "p21-contract-review", "p21-left-review", "p21-integration-left", "p21-integration-right"],
  late: ["p21-partner-selection", "p21-left-review", "p21-center-review", "p21-right-review", "p21-integration-left", "p21-integration-right"],
  recovery: ["p21-integration-left", "p21-integration-right", "p21-settlement-left", "p21-settlement-right"],
} as const;

// 各案件の負荷は、対応する投資施策を選ぶ理由になる。
// 同じ分岐で同系統ばかりにならないよう、案件名ではなくこの系統で散らす。
const CASE_ARCHETYPE: Record<string, string> = {
  "p21-capex": "fatigue",
  "p21-roi": "backlog",
  "p21-automation-pilot": "rollback",
  "p21-data-integration": "backlog",
  "p21-automation-scale": "rollback",
  "p21-left-review": "squeeze",
  "p21-partner-selection": "backlog",
  "p21-knowledge-transfer": "backlog",
  "p21-center-review": "fatigue",
  "p21-risk-assessment": "fatigue",
  "p21-resilience-build": "fatigue",
  "p21-contract-review": "squeeze",
  "p21-right-review": "squeeze",
  "p21-integration-left": "rollback",
  "p21-integration-right": "fatigue",
  "p21-settlement-left": "squeeze",
  "p21-settlement-right": "backlog",
};

type Cell = { x: number; y: number };
type Edge = { from: Cell; to: Cell };
type GeneratedKind = Exclude<NodeDefinition["kind"], "start">;

function targetProgressPerDeadline(y: number): number {
  // 値は、進捗戻し込みでプレイヤーに要求したい実質進捗/工期。
  // 初手は初期デッキと巻き戻しを監査して2.5から始める。
  // 最初の5段は+0.5、次の5段は+0.75、後半は+1.0で伸ばす。
  // PJ間に休息・レリックが入っても、中盤の出力要求が急に跳ねにくい勾配にする。
  if (y <= 5) return 2.5 + (y - 1) * 0.5;
  if (y <= 10) return 4.5 + (y - 5) * 0.75;
  return 8.25 + (y - 10);
}

function deadlineFor(y: number, baseDeadline: number): number {
  // 後半は必要進捗だけを大きくせず、投資を回収するための運用期間も渡す。
  if (y >= 14) return 8;
  if (y >= 11) return 7;
  return baseDeadline;
}

function key({ x, y }: Cell): string {
  return `${x}:${y}`;
}

function createRng(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x1_0000_0000;
  };
}

function shuffled<T>(items: readonly T[], rng: () => number): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(rng() * (index + 1));
    [result[index], result[swap]] = [result[swap], result[index]];
  }
  return result;
}

function crosses(candidate: Edge, existing: readonly Edge[]): boolean {
  return existing.some((edge) =>
    candidate.from.x !== edge.from.x
    && candidate.to.x !== edge.to.x
    && (candidate.from.x - edge.from.x) * (candidate.to.x - edge.to.x) < 0,
  );
}

/**
 * 初代StSと同じ発想で、空の7列グリッドに縦断パスを重ねる。
 * マス数を先に決めず、各パスが次行の近い3列のどこへ進むかで地形を作る。
 */
export function generateInvestmentMap(seed = 20260902): Record<string, NodeDefinition> {
  const rng = createRng(seed);
  const cells = new Map<string, Cell>();
  const edges: Edge[] = [];
  const edgesByRow = new Map<number, Edge[]>();

  function addCell(cell: Cell): void {
    cells.set(key(cell), cell);
  }
  function addEdge(from: Cell, to: Cell): void {
    edges.push({ from, to });
    const rowEdges = edgesByRow.get(from.y) ?? [];
    rowEdges.push({ from, to });
    edgesByRow.set(from.y, rowEdges);
  }

  const starts: Cell[] = [];
  for (let path = 0; path < PATH_COUNT; path += 1) {
    let column = Math.floor(rng() * WIDTH);
    if (path === 1) while (column === starts[0].x) column = Math.floor(rng() * WIDTH);
    let current = { x: column, y: 1 };
    starts.push(current);
    addCell(current);

    for (let y = 1; y < LAST_ROW; y += 1) {
      const choices = shuffled([current.x - 1, current.x, current.x + 1]
        .filter((x) => x >= 0 && x < WIDTH), rng);
      const nextColumn = choices.find((x) => !crosses(
        { from: current, to: { x, y: y + 1 } },
        edgesByRow.get(y) ?? [],
      )) ?? current.x;
      const next = { x: nextColumn, y: y + 1 };
      addCell(next);
      addEdge(current, next);
      current = next;
    }
  }

  const start = { x: 3, y: 0 };
  const boss = { x: 3, y: LAST_ROW + 1 };
  addCell(start);
  addCell(boss);
  for (const cell of new Map(starts.map((cell) => [key(cell), cell])).values()) addEdge(start, cell);
  for (const cell of cells.values()) if (cell.y === LAST_ROW) addEdge(cell, boss);

  const ordered = [...cells.values()]
    .filter((cell) => cell.y > 0 && cell.y < boss.y)
    .sort((a, b) => a.y - b.y || a.x - b.x);

  const parentsByCell = new Map<string, Cell[]>();
  for (const edge of edges) {
    if (edge.from.y === 0 || edge.to.y === boss.y) continue;
    const parents = parentsByCell.get(key(edge.to)) ?? [];
    parents.push(edge.from);
    parentsByCell.set(key(edge.to), parents);
  }

  // 種別は地形生成とは別に、全体の比率と局所の分岐を見て決める。
  // 案件が主役で、休息・報酬・イベントは「ルートを選ぶ理由」として少数置く。
  const roomKinds = new Map<string, GeneratedKind>();
  const openingCells = ordered.filter((cell) => cell.y <= 3);
  for (const cell of openingCells) roomKinds.set(key(cell), "case");

  const laterCells = ordered.filter((cell) => cell.y > 3);
  const specialRemaining: Record<"event" | "rest" | "relic", number> = {
    event: Math.max(5, Math.round(ordered.length * 0.12)),
    rest: Math.max(5, Math.round(ordered.length * 0.13)),
    relic: Math.max(4, Math.round(ordered.length * 0.1)),
  };

  for (const [index, cell] of laterCells.entries()) {
    const slotsRemaining = laterCells.length - index;
    const specialCount = Object.values(specialRemaining).reduce((sum, count) => sum + count, 0);
    const siblingKinds = new Map<string, number>();
    for (const parent of parentsByCell.get(key(cell)) ?? []) {
      for (const sibling of edges.filter((edge) => key(edge.from) === key(parent)).map((edge) => edge.to)) {
        const siblingKind = roomKinds.get(key(sibling));
        if (siblingKind) siblingKinds.set(siblingKind, (siblingKinds.get(siblingKind) ?? 0) + 1);
      }
    }
    const parentKinds = (parentsByCell.get(key(cell)) ?? []).map((parent) => roomKinds.get(key(parent)));

    const choices: GeneratedKind[] = ["case"];
    for (const kind of ["event", "rest", "relic"] as const) if (specialRemaining[kind] > 0) choices.push(kind);
    const forcedSpecial = specialCount >= slotsRemaining;
    const kind = choices
      .filter((candidate) => !forcedSpecial || candidate !== "case")
      .map((candidate) => {
        const base = candidate === "case"
          ? (slotsRemaining - specialCount) / slotsRemaining
          : specialRemaining[candidate] / slotsRemaining;
        const parentPenalty = parentKinds.includes(candidate) ? 0.18 : 0;
        const siblingPenalty = (siblingKinds.get(candidate) ?? 0) * 0.28;
        return { candidate, score: base - parentPenalty - siblingPenalty + rng() * 0.02 };
      })
      .sort((a, b) => b.score - a.score)[0].candidate;
    roomKinds.set(key(cell), kind);
    if (kind !== "case") specialRemaining[kind] -= 1;
  }

  const caseCells = new Map<string, string>();
  const casePoolFor = (cell: Cell): readonly string[] => {
    if (cell.y === 1) return CASE_POOLS.opening;
    if (cell.y === 2) return CASE_POOLS.openingFollowup;
    if (cell.y <= 4) return CASE_POOLS.earlySafe;
    if (cell.y <= 5) return CASE_POOLS.early;
    if (cell.y <= 8) return CASE_POOLS.mid;
    if (cell.y <= 11) return CASE_POOLS.late;
    return CASE_POOLS.recovery;
  };
  const caseCursorByPool = new Map<string, number>();
  for (const cell of ordered.filter((candidate) => roomKinds.get(key(candidate)) === "case")) {
    const pool = casePoolFor(cell);
    const poolKey = pool.join("|");
    const cursor = caseCursorByPool.get(poolKey) ?? 0;
    const parentCaseIds = (parentsByCell.get(key(cell)) ?? []).map((parent) => caseCells.get(key(parent)));
    const parentArchetypes = parentCaseIds.flatMap((caseId) => caseId ? [CASE_ARCHETYPE[caseId]] : []);
    const siblingCaseIds = (parentsByCell.get(key(cell)) ?? []).flatMap((parent) =>
      edges
        .filter((edge) => key(edge.from) === key(parent))
        .map((edge) => caseCells.get(key(edge.to)))
        .filter((caseId): caseId is string => Boolean(caseId)),
    );
    const siblingArchetypes = siblingCaseIds.map((caseId) => CASE_ARCHETYPE[caseId]);
    const candidates = [...pool.slice(cursor), ...pool.slice(0, cursor)];
    const caseId = candidates.find((candidate) =>
      !parentArchetypes.includes(CASE_ARCHETYPE[candidate])
      && !siblingArchetypes.includes(CASE_ARCHETYPE[candidate]),
    ) ?? candidates.find((candidate) => !siblingArchetypes.includes(CASE_ARCHETYPE[candidate]))
      ?? candidates.find((candidate) => !parentCaseIds.includes(candidate))
      ?? pool[cursor];
    caseCells.set(key(cell), caseId);
    caseCursorByPool.set(poolKey, (pool.indexOf(caseId) + 1) % pool.length);
  }

  const nextByCell = new Map<string, string[]>();
  for (const edge of edges) {
    const list = nextByCell.get(key(edge.from)) ?? [];
    list.push(key(edge.to));
    nextByCell.set(key(edge.from), list);
  }

  const nodeId = (cell: Cell): string => {
    if (cell.y === 0) return "start";
    if (cell.y === boss.y) return "boss";
    return `p21-path-${cell.y}-${cell.x}`;
  };
  const kindFor = (cell: Cell): NodeDefinition["kind"] => {
    return roomKinds.get(key(cell)) ?? "event";
  };

  return Object.fromEntries([...cells.values()].map((cell) => {
    const kind = cell.y === 0 ? "start" : cell.y === boss.y ? "case" : kindFor(cell);
    const next = [...new Set(nextByCell.get(key(cell)) ?? [])].map((value) => {
      const [x, y] = value.split(":").map(Number);
      return nodeId({ x, y });
    });
    const caseId = cell.y === boss.y ? "investment-boss" : caseCells.get(key(cell));
    // 追加で必要になる進捗を1/工期ぶん見込み、
    // 実質要求値 = (表示上の必要進捗 / 工期) + 1 として難度を揃える。
    const deadline = kind === "case" && caseId ? deadlineFor(cell.y, CASES[caseId].deadline) : undefined;
    const required = kind === "case" && caseId && deadline
      ? Math.max(1, Math.round((targetProgressPerDeadline(cell.y) - 1) * deadline))
      : undefined;
    return [nodeId(cell), {
      id: nodeId(cell), kind, x: cell.x, y: cell.y, next,
      ...(kind === "case" && caseId ? { caseId, required, deadline } : {}),
    } satisfies NodeDefinition];
  }));
}

// 固定seed。ステージ選択を開くたび形が変わらないようにし、まずは地形生成だけ試す。
export const MAP_NODES_INVESTMENT = generateInvestmentMap();
